import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, ClipboardList, Brain, Users, FileText, CheckCircle, AlertCircle } from "lucide-react";
import { ASSESSMENTS } from "@/constants/testIds";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

// Clinical Reference Grid Data
const assessmentBattery = [
  {
    name: "ADOS-2 (Modules 1-4)",
    ageRange: "12mo – Adult",
    clinicalPurpose: "Gold-standard observational ASD identification",
    price: "₹22,000 – ₹25,000"
  },
  {
    name: "WISC-IV / V",
    ageRange: "6 – 16 Yrs",
    clinicalPurpose: "Cognitive, working memory & processing speed",
    price: "₹12,000 – ₹15,000"
  },
  {
    name: "Vineland-3",
    ageRange: "Birth – 90+",
    clinicalPurpose: "Functional adaptive behavior mapping",
    price: "₹8,000 – ₹10,000"
  },
  {
    name: "ISAA / Com-DEALL",
    ageRange: "All Ages",
    clinicalPurpose: "Statutory compliance & disability certification",
    price: "₹10,000 – ₹12,000"
  },
  {
    name: "Executive Function Profile",
    ageRange: "6 – Adult",
    clinicalPurpose: "ADHD-related behavioral regulation indices",
    price: "₹7,000 – ₹9,000"
  },
  {
    name: "BRIEF-2 / Conners 4",
    ageRange: "5 – 18 Yrs",
    clinicalPurpose: "Multi-informant executive functioning & ADHD",
    price: "₹6,000 – ₹8,000"
  },
  {
    name: "Sensory Profile 2",
    ageRange: "3 – 14 Yrs",
    clinicalPurpose: "Environmental sensory mapping for behavioral triggers",
    price: "₹5,000 – ₹7,000"
  },
  {
    name: "CELF-5",
    ageRange: "5 – 21 Yrs",
    clinicalPurpose: "Linguistic-social communication competence",
    price: "₹8,000 – ₹10,000"
  },
];

const processSteps = [
  {
    step: 1,
    icon: ClipboardList,
    title: "Clinical Intake & History Mapping",
    duration: "60 minutes",
    description: "Comprehensive caregiver interview establishing developmental narrative and assessment objectives.",
  },
  {
    step: 2,
    icon: Brain,
    title: "Psychometric Battery Administration",
    duration: "1–3 sessions",
    description: "Gold-standard instrumentation administered using observer-led methodology.",
  },
  {
    step: 3,
    icon: Users,
    title: "Multi-Informant Data Synthesis",
    duration: "Data triangulation",
    description: "Integration of caregiver, educator, and clinician observation data.",
  },
  {
    step: 4,
    icon: FileText,
    title: "Biopsychosocial Formulation",
    duration: "60 minutes",
    description: "Comprehensive feedback session with diagnostic conclusions and intervention pathways.",
  },
];

export const AssessmentsPage = () => {
  return (
    <div data-testid={ASSESSMENTS.section}>
      <Helmet>
        <title>Diagnostic Assessments | Modern Minds - ADOS-2, WISC, Vineland Testing in Lucknow</title>
        <meta name="description" content="Gold-standard neurodevelopmental assessments including ADOS-2, WISC-IV/V, BRIEF-2, Vineland-3. Observer-led diagnostic mapping for autism, ADHD, learning disabilities at Modern Minds, Lucknow." />
        <link rel="canonical" href="https://www.modernminds.co.in/assessments" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <motion.div variants={fadeInUp} className="gradient-divider mx-auto mb-6" />
            <motion.span
              variants={fadeInUp}
              className="inline-block bg-[#C9A962]/20 text-[#C9A962] text-xs font-body font-semibold px-3 py-1 rounded mb-4"
            >
              DIAGNOSTIC ASSESSMENTS
            </motion.span>
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            >
              The Grassroots Protocol
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-body text-base sm:text-lg text-[#E2E8F0] max-w-2xl mx-auto"
            >
              High-resolution diagnostic mapping using gold-standard instrumentation and observer-led methodology.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Clinical Reference Grid - Assessment Battery */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-6"
          >
            <div className="gradient-divider mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
              Clinical Reference Data
            </h2>
            <p className="font-body text-sm text-[#778DA9]">
              Assessment battery for referring physicians and families
            </p>
          </motion.div>

          {/* Desktop Table View */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="hidden md:block assessment-grid"
          >
            <table className="w-full">
              <thead>
                <tr>
                  <th className="font-body">Clinical Protocol</th>
                  <th className="font-body">Age/Target</th>
                  <th className="font-body">Diagnostic Scope & Utility</th>
                  <th className="font-body">Fee Range</th>
                </tr>
              </thead>
              <tbody>
                {assessmentBattery.map((item) => (
                  <tr key={item.name}>
                    <td className="font-body font-semibold text-white">{item.name}</td>
                    <td className="font-body text-[#778DA9]">{item.ageRange}</td>
                    <td className="font-body">{item.clinicalPurpose}</td>
                    <td className="font-body font-semibold text-[#C9A962]">{item.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {/* Mobile Card Stack View */}
          <div className="md:hidden space-y-3">
            {assessmentBattery.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="glass-card p-4"
              >
                <h4 className="font-body font-semibold text-white text-sm mb-2">{item.name}</h4>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="font-body text-[#778DA9]">Age Range:</span>
                    <span className="font-body text-[#E0E1DD]">{item.ageRange}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-body text-[#778DA9]">Purpose:</span>
                    <span className="font-body text-[#E0E1DD] text-right max-w-[60%]">{item.clinicalPurpose}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span className="font-body text-[#778DA9]">Fee:</span>
                    <span className="font-body font-semibold text-[#C9A962]">{item.price}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-6 flex items-start gap-3 glass-card p-4"
          >
            <AlertCircle size={18} className="text-[#C9A962] mt-0.5 flex-shrink-0" />
            <p className="font-body text-xs text-[#E0E1DD] italic">
              *Fees are customized based on the specific testing battery required. A detailed estimate will be provided after your initial consultation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Assessment Process Timeline */}
      <section className="py-8 sm:py-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <div className="gradient-divider mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
              Assessment Workflow
            </h2>
          </motion.div>

          <div data-testid={ASSESSMENTS.timeline} className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 timeline-connector hidden md:block" />

            {processSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="relative pl-0 md:pl-16 pb-6 last:pb-0"
                >
                  <div className="hidden md:flex absolute left-0 w-12 h-12 bg-[#C9A962] rounded flex items-center justify-center">
                    <IconComponent size={20} className="text-[#0D1B2A]" />
                  </div>

                  <div className="glass-card p-5">
                    <div className="flex md:hidden items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-[#C9A962] rounded flex items-center justify-center">
                        <IconComponent size={18} className="text-[#0D1B2A]" />
                      </div>
                      <span className="font-body text-xs text-[#C9A962] font-semibold">
                        STEP {step.step}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <h3 className="font-heading text-lg font-bold text-white">
                        {step.title}
                      </h3>
                      <span className="font-body text-xs text-[#C9A962] bg-[#C9A962]/20 px-3 py-1 rounded w-fit font-semibold">
                        {step.duration}
                      </span>
                    </div>
                    <p className="font-body text-sm text-[#E0E1DD] leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="glass-card-gold p-8 text-center"
          >
            <div className="gradient-divider mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
              Initiate Diagnostic Mapping
            </h2>
            <p className="font-body text-sm text-[#E0E1DD] mb-6 max-w-xl mx-auto">
              Schedule your child&apos;s clinical intake session to begin the Grassroots Protocol and establish comprehensive biopsychosocial architecture.
            </p>
            <a
              href="https://consult.modernminds.co.in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 btn-gold rounded px-8 py-3 font-body font-semibold"
            >
              Schedule Clinical Intake
              <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
